import supabase from '../supabase/supabase';

const TABLES = {
  users: 'users_1715680000000',
  learners: 'hms_academy_learners_1715725000000',
  organizations: 'organizations_1715680000000',
  courses: 'hms_academy_courses_1715725000000',
  versions: 'hms_academy_course_versions_1715725000000',
  enrollments: 'hms_academy_enrollments_1715726000000'
};

const LEARNER_STATUSES = ['active', 'inactive', 'archived'];
const ADMIN_ROLES = ['SUPERADMIN', 'COMPANY_ADMIN'];

function serviceError(code, message, cause) {
  const error = new Error(message);
  error.code = code;
  if (cause) error.cause = cause;
  return error;
}

function requireData(result, action) {
  if (result.error) {
    throw serviceError(
      result.error.code || 'SUPABASE_ERROR',
      describeDatabaseError(result.error, action),
      result.error
    );
  }
  return result.data;
}

function describeDatabaseError(error, action) {
  if (error.code === '23505') {
    return 'Deltakeren har allerede en åpen påmelding til denne kursversjonen.';
  }
  if (error.code === '23503') {
    return 'Valgt organisasjon, deltaker eller kursversjon finnes ikke lenger.';
  }
  if (error.code === '23514') {
    return `Databasens Academy-regler avviste ${action}. Kontroller deltakerens status og organisasjons- og kurskoblingen.`;
  }
  if (error.code === '42501' || error.code === 'PGRST301') {
    return `Du har ikke databaseautorisasjon til å ${action}.`;
  }
  return `Kunne ikke ${action}: ${error.message}`;
}

async function getAdminContext() {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) {
    throw serviceError('AUTH_ERROR', authError.message, authError);
  }
  if (!authData?.user) {
    throw serviceError('NO_AUTH_SESSION', 'En innlogget administrator må utføre handlingen.');
  }

  const profile = requireData(
    await supabase
      .from(TABLES.users)
      .select('id,role,status,organization_id')
      .eq('id', authData.user.id)
      .maybeSingle(),
    'hente administratorprofilen'
  );

  if (!profile || profile.status !== 'active' || !ADMIN_ROLES.includes(profile.role)) {
    throw serviceError(
      'ACADEMY_ADMIN_REQUIRED',
      'En aktiv SUPERADMIN- eller COMPANY_ADMIN-profil er nødvendig.'
    );
  }

  return { user: authData.user, profile };
}

function validateLearnerInput(input) {
  const firstName = input.firstName?.trim();
  const lastName = input.lastName?.trim();
  const email = input.email?.trim();
  if (!firstName || !lastName || !email) {
    throw serviceError('INVALID_INPUT', 'Fornavn, etternavn og e-postadresse må fylles ut.');
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw serviceError('INVALID_INPUT', 'Skriv inn en gyldig e-postadresse.');
  }
  if (!LEARNER_STATUSES.includes(input.status)) {
    throw serviceError('INVALID_INPUT', 'Velg en gyldig deltakerstatus.');
  }
  return { firstName, lastName, email };
}

export async function createAcademyParticipant(input) {
  const { user, profile } = await getAdminContext();
  const { firstName, lastName, email } = validateLearnerInput(input);
  const organizationId = input.organizationId || null;

  if (profile.role === 'COMPANY_ADMIN') {
    if (!profile.organization_id || organizationId !== profile.organization_id) {
      throw serviceError(
        'ORGANIZATION_SCOPE',
        'COMPANY_ADMIN kan bare registrere deltakere for egen organisasjon.'
      );
    }
  }

  const inserted = requireData(
    await supabase
      .from(TABLES.learners)
      .insert({
        organization_id: organizationId,
        user_id: null,
        first_name: firstName,
        last_name: lastName,
        email,
        status: input.status
      })
      .select('id')
      .single(),
    'registrere deltakeren'
  );

  return inserted;
}

async function readLearnerForAssignment(learnerId, profile) {
  const learner = requireData(
    await supabase
      .from(TABLES.learners)
      .select('id,organization_id,status')
      .eq('id', learnerId)
      .maybeSingle(),
    'lese deltakeren'
  );

  if (!learner) {
    throw serviceError('LEARNER_NOT_FOUND', 'Deltakeren finnes ikke eller er ikke tilgjengelig via RLS.');
  }
  if (learner.status !== 'active') {
    throw serviceError('LEARNER_INACTIVE', 'Bare aktive deltakere kan tildeles kurs.');
  }
  if (
    profile.role === 'COMPANY_ADMIN' &&
    learner.organization_id !== profile.organization_id
  ) {
    throw serviceError('ORGANIZATION_SCOPE', 'Du kan bare tildele kurs til deltakere i egen organisasjon.');
  }

  return learner;
}

function learnerCanTakeCourse(learner, course) {
  if (learner.organization_id === null) return course.organization_id === null;
  return course.organization_id === null ||
    course.organization_id === learner.organization_id;
}

export async function getAcademyAssignmentOptions(learnerId) {
  const { profile } = await getAdminContext();
  const learner = await readLearnerForAssignment(learnerId, profile);

  const [courseResult, versionResult, enrollmentResult] = await Promise.all([
    supabase
      .from(TABLES.courses)
      .select('id,title,organization_id,status')
      .eq('status', 'approved')
      .order('title'),
    supabase
      .from(TABLES.versions)
      .select('id,course_id,version,title,language_code,status')
      .eq('status', 'approved')
      .order('version', { ascending: false }),
    supabase
      .from(TABLES.enrollments)
      .select('course_version_id,status')
      .eq('learner_id', learner.id)
      .in('status', ['pending', 'active'])
  ]);

  const courses = requireData(courseResult, 'lese godkjente Academy-kurs');
  const versions = requireData(versionResult, 'lese godkjente kursversjoner');
  const enrollments = requireData(enrollmentResult, 'kontrollere eksisterende påmeldinger');

  const eligibleCourses = courses
    .filter((course) => learnerCanTakeCourse(learner, course))
    .map((course) => ({
      ...course,
      versions: versions.filter(
        (version) => version.course_id === course.id && version.status === 'approved'
      )
    }))
    .filter((course) => course.versions.length > 0);

  return {
    courses: eligibleCourses,
    openCourseVersionIds: enrollments.map((enrollment) => enrollment.course_version_id)
  };
}

export async function assignAcademyCourse({ learnerId, courseVersionId }) {
  const { user, profile } = await getAdminContext();
  const learner = await readLearnerForAssignment(learnerId, profile);
  if (!courseVersionId) {
    throw serviceError('INVALID_INPUT', 'Velg en godkjent kursversjon.');
  }

  const version = requireData(
    await supabase
      .from(TABLES.versions)
      .select('id,course_id,status')
      .eq('id', courseVersionId)
      .maybeSingle(),
    'kontrollere kursversjonen'
  );
  if (!version || version.status !== 'approved') {
    throw serviceError('COURSE_VERSION_INVALID', 'Kursversjonen finnes ikke eller er ikke godkjent.');
  }

  const course = requireData(
    await supabase
      .from(TABLES.courses)
      .select('id,organization_id,status')
      .eq('id', version.course_id)
      .maybeSingle(),
    'kontrollere kurset'
  );
  if (!course || course.status !== 'approved' || !learnerCanTakeCourse(learner, course)) {
    throw serviceError(
      'COURSE_SCOPE',
      'Kurset er ikke godkjent eller kan ikke tildeles denne deltakerens organisasjon.'
    );
  }

  const existing = requireData(
    await supabase
      .from(TABLES.enrollments)
      .select('id,status')
      .eq('learner_id', learner.id)
      .eq('course_version_id', version.id)
      .in('status', ['pending', 'active'])
      .limit(1),
    'kontrollere åpne påmeldinger'
  );
  if (existing.length) {
    throw serviceError(
      'OPEN_ENROLLMENT_EXISTS',
      'Deltakeren har allerede en åpen påmelding til denne kursversjonen.'
    );
  }

  return requireData(
    await supabase
      .from(TABLES.enrollments)
      .insert({
        learner_id: learner.id,
        course_version_id: version.id,
        source: 'admin_assignment',
        status: 'pending',
        created_by: user.id
      })
      .select('id,status,created_at')
      .single(),
    'opprette påmeldingen'
  );
}